import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  SimpleGrid,
  HStack,
  Text,
  Divider,
  useToast,
  Switch,
  Box,
  RadioGroup,
  Radio,
  Stack,
  Badge,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useJsApiLoader } from "@react-google-maps/api";
import axios from "axios";
import { Car, HireDraft } from "./car.types";
import AutocompleteInput from "../../../LocationsModule/PlaceAutocompleteInput";
import { CustomDatePicker } from "../../CustomReusables/CustomDatePicker";
import CustomTimePicker from "../../CustomReusables/CustomTimePicker";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import AppPhoneInput, {
  AppPhoneCountryCode,
  buildInternationalNumber,
} from "../../Shared/PhoneInput";
const CARS_API = process.env.REACT_APP_BACKEND_URL + "/api/v1/travel/cars";
const cardsBorderRadii = "8px";
type Coords = { lat: number; lng: number };
type SuccessfulHireQuote = NonNullable<HireDraft["quote"]>;
type FailedHireQuote = {
  ok: false;
  error: string;
};
type HireQuoteState = SuccessfulHireQuote | FailedHireQuote | null;
const isFailedHireQuote = (value: HireQuoteState): value is FailedHireQuote => {
  return Boolean(value && "ok" in value && value.ok === false);
};
const isSuccessfulHireQuote = (
  value: HireQuoteState,
): value is SuccessfulHireQuote => {
  return Boolean(value && "breakdown" in value);
};
const SERVICE_FEE_RATE = 0.05;
const roundMoney = (value: number) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
function normalizeDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}
function enumerateDays(start: Date, end: Date) {
  const days: Date[] = [];
  const d = new Date(start);
  d.setHours(0, 0, 0, 0);
  const e = new Date(end);
  e.setHours(0, 0, 0, 0);
  while (d <= e) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}
function parseLocalDate(value?: string) {
  const clean = String(value || "").trim();
  if (!clean) return null;
  const dateOnly = clean.slice(0, 10);
  const match = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}
function extractTimeFromDateTime(value?: string) {
  const clean = String(value || "").trim();
  if (!clean) return "";
  const match = clean.match(/T(\d{2}):(\d{2})/);
  if (!match) return "";
  return `${match[1]}:${match[2]}`;
}
function getInitialPickupDate(car: any) {
  return parseLocalDate(car?.selectedPickupDate || car?.selectedPickupAt);
}
function getInitialReturnDate(car: any) {
  return parseLocalDate(car?.selectedReturnDate || car?.selectedReturnAt);
}
function getInitialPickupTime(car: any) {
  return (
    String(car?.selectedPickupTime || "").trim() ||
    extractTimeFromDateTime(car?.selectedPickupAt) ||
    "09:00"
  );
}
function getInitialReturnTime(car: any) {
  return (
    String(car?.selectedReturnTime || "").trim() ||
    extractTimeFromDateTime(car?.selectedReturnAt) ||
    "09:00"
  );
}
function toLocalDateString(date: Date | null) {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function toISOWithTime(date: Date, timeHHmm: string) {
  const localDate = toLocalDateString(date);
  return `${localDate}T${timeHHmm}:00`;
}
export default function HireCarModal({
  isOpen,
  onClose,
  car,
}: {
  isOpen: boolean;
  onClose: () => void;
  car: Car | null;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const { theme } = useCustomTheme();
  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-hire-modal-zindex-fix", "true");
    style.innerHTML = `
      .pac-container {
        z-index: 30001 !important;
      }
      .react-datepicker-popper,
      .react-datepicker,
      .react-datepicker__portal,
      .react-datepicker__tab-loop {
        z-index: 30000 !important;
      }
      .react-datepicker-wrapper,
      .react-datepicker__input-container {
        width: 100% !important;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);
  const [pickupDate, setPickupDate] = useState<Date | null>(null);
  const [returnDate, setReturnDate] = useState<Date | null>(null);
  const [pickupTime, setPickupTime] = useState("09:00");
  const [returnTime, setReturnTime] = useState("09:00");
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupCoords, setPickupCoords] = useState<Coords | null>(null);
  const [deliveryRequested, setDeliveryRequested] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryCoords, setDeliveryCoords] = useState<Coords | null>(null);
  const [phoneCountryCode, setPhoneCountryCode] =
    useState<AppPhoneCountryCode>("MW");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [age, setAge] = useState(21);
  const [insurancePlan, setInsurancePlan] = useState<
    "none" | "basic" | "premium"
  >("none");
  const [promoCode, setPromoCode] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [licenseStatus, setLicenseStatus] = useState<
    "unverified" | "pending" | "verified" | "rejected"
  >("unverified");
  const [availabilityRanges, setAvailabilityRanges] = useState<any[]>([]);
  const [quote, setQuote] = useState<HireQuoteState>(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [dateValidationAttempted, setDateValidationAttempted] = useState(false);
  const [continueAttempted, setContinueAttempted] = useState(false);
  useEffect(() => {
    if (!isOpen || !car?._id) return;
    const carData = car as any;
    const initialPickupDate = getInitialPickupDate(carData);
    const initialReturnDate = getInitialReturnDate(carData);
    setPickupDate(initialPickupDate);
    setReturnDate(initialReturnDate);
    setPickupTime(getInitialPickupTime(carData));
    setReturnTime(getInitialReturnTime(carData));
    setDateValidationAttempted(true);
    setPickupAddress(String(carData?.pickupLocation || ""));
    setPickupCoords(
      carData?.pickupLocationCoords &&
        typeof carData.pickupLocationCoords.lat === "number" &&
        typeof carData.pickupLocationCoords.lng === "number"
        ? carData.pickupLocationCoords
        : null,
    );
    setDeliveryRequested(false);
    setDeliveryAddress("");
    setDeliveryCoords(null);
    setPhoneCountryCode("MW");
    setPhoneNumber("");
    setNotes("");
    setAge(21);
    setInsurancePlan("none");
    setPromoCode("");
    setLicenseNumber("");
    setLicenseStatus("unverified");
    setContinueAttempted(false);
    setQuote(null);
  }, [isOpen, car?._id]);
  useEffect(() => {
    if (!isOpen || !car?._id) return;
    (async () => {
      try {
        const res = await axios.get(`${CARS_API}/hire/availability/${car._id}`);
        if (res.data?.ok) {
          setAvailabilityRanges(res.data.ranges || []);
        }
      } catch {
        setAvailabilityRanges([]);
      }
    })();
  }, [isOpen, car?._id]);
  const blockedDaySet = useMemo(() => {
    const set = new Set<number>();
    for (const r of availabilityRanges) {
      const start = new Date(r.pickupAt);
      const end = new Date(r.returnAt);
      enumerateDays(start, end).forEach((d) => set.add(normalizeDay(d)));
    }
    return set;
  }, [availabilityRanges]);
  const filterAvailableDate = (d: Date) => !blockedDaySet.has(normalizeDay(d));
  const dateValidation = useMemo(() => {
    let pickupError = "";
    let returnError = "";
    if (!pickupDate) pickupError = "Pickup date is required.";
    if (!returnDate) returnError = "Return date is required.";
    if (pickupDate && blockedDaySet.has(normalizeDay(pickupDate))) {
      pickupError = "The selected pickup date is unavailable.";
    }
    if (returnDate && blockedDaySet.has(normalizeDay(returnDate))) {
      returnError = "The selected return date is unavailable.";
    }
    if (pickupDate && returnDate) {
      const pickupAt = toISOWithTime(pickupDate, pickupTime);
      const returnAt = toISOWithTime(returnDate, returnTime);
      const pickup = new Date(pickupAt);
      const ret = new Date(returnAt);
      if (Number.isNaN(pickup.getTime())) {
        pickupError = "Select a valid pickup date and time.";
      } else if (pickup.getTime() < Date.now() - 60 * 1000) {
        pickupError = "Pickup date and time cannot be in the past.";
      }
      if (Number.isNaN(ret.getTime())) {
        returnError = "Select a valid return date and time.";
      } else if (!pickupError && ret <= pickup) {
        returnError = "Return date and time must be after pickup.";
      }
    }
    return {
      pickupError,
      returnError,
      isValid: !pickupError && !returnError,
    };
  }, [pickupDate, returnDate, pickupTime, returnTime, blockedDaySet]);
  useEffect(() => {
    if (!car?._id || !pickupDate || !returnDate || !dateValidation.isValid) {
      setQuote(null);
      return;
    }
    const pickupAt = toISOWithTime(pickupDate, pickupTime);
    const returnAt = toISOWithTime(returnDate, returnTime);
    const pickup = new Date(pickupAt);
    const ret = new Date(returnAt);
    if (
      Number.isNaN(pickup.getTime()) ||
      Number.isNaN(ret.getTime()) ||
      ret <= pickup
    ) {
      setQuote(null);
      return;
    }
    setLoadingQuote(true);
    (async () => {
      try {
        const res = await axios.post(`${CARS_API}/hire/quote`, {
          carId: car._id,
          pickupAt,
          returnAt,
          insurancePlan,
          deliveryRequested,
          promoCode,
        });
        const quoteData = res.data?.data || res.data;
        if (!quoteData?.breakdown) {
          throw new Error(
            quoteData?.error ||
              "The quote response did not include a breakdown.",
          );
        }
        setQuote(quoteData as SuccessfulHireQuote);
      } catch (e: any) {
        setQuote({
          ok: false,
          error: e?.response?.data?.error || e?.message || "Quote failed",
        });
      } finally {
        setLoadingQuote(false);
      }
    })();
  }, [
    car?._id,
    pickupDate,
    returnDate,
    pickupTime,
    returnTime,
    dateValidation.isValid,
    insurancePlan,
    deliveryRequested,
    promoCode,
  ]);
  const fullPhoneNumber = useMemo(
    () => buildInternationalNumber(phoneCountryCode, phoneNumber),
    [phoneCountryCode, phoneNumber],
  );
  if (!car) return null;
  const inputStyles = {
    borderColor: theme.inputBorder,
    bg: theme.inputBg,
    color: theme.text,
    _placeholder: { color: theme.inputPlaceholder },
    _hover: { borderColor: theme.inputBorderFocus },
    _focus: {
      borderColor: theme.inputBorderFocus,
      boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
    },
  };
  const phoneDigits = fullPhoneNumber.replace(/\D/g, "");
  const phoneNumberError = !phoneNumber.trim()
    ? "Phone number is required."
    : phoneDigits.length < 8 || phoneDigits.length > 15
      ? "Enter a valid phone number."
      : "";
  const normalizedLicenseNumber = licenseNumber.trim();
  const licenseNumberError = !normalizedLicenseNumber
    ? "Driving licence number is required."
    : normalizedLicenseNumber.length < 5
      ? "Enter a valid driving licence number."
      : !/^[A-Za-z0-9./\- ]+$/.test(normalizedLicenseNumber)
        ? "Driving licence number contains invalid characters."
        : "";
  const submit = () => {
    setDateValidationAttempted(true);
    setContinueAttempted(true);
    if (!dateValidation.isValid || !pickupDate || !returnDate) {
      return toast({
        title:
          dateValidation.pickupError ||
          dateValidation.returnError ||
          "Pickup and return dates are required.",
        status: "warning",
      });
    }
    if (loadingQuote) {
      return toast({
        title: "Checking availability",
        description: "Please wait while the selected hire dates are verified.",
        status: "info",
      });
    }
    if (!isSuccessfulHireQuote(quote)) {
      return toast({
        title: isFailedHireQuote(quote)
          ? quote.error || "Car unavailable for these dates"
          : "Choose valid hire dates",
        description: isFailedHireQuote(quote)
          ? "Please choose another valid pickup and return period."
          : "A valid availability quote is required before checkout.",
        status: "warning",
      });
    }
    if (phoneNumberError) {
      return toast({
        title: "Phone number required",
        description: phoneNumberError,
        status: "warning",
      });
    }
    if (licenseNumberError) {
      return toast({
        title: "Driving licence required",
        description: licenseNumberError,
        status: "warning",
      });
    }
    if (!age || age < 21) {
      return toast({
        title: "Minimum age is 21",
        status: "warning",
      });
    }
    const pickupAt = toISOWithTime(pickupDate, pickupTime);
    const returnAt = toISOWithTime(returnDate, returnTime);
    const hire: HireDraft = {
      carId: car._id,
      pickupAt,
      returnAt,
      pickupTime,
      returnTime,
      pickupAddress: {
        text: pickupAddress || car.pickupLocation || "",
        ...(pickupCoords || {}),
      },
      deliveryRequested,
      deliveryAddress: deliveryRequested
        ? {
            text: deliveryAddress,
            ...(deliveryCoords || {}),
          }
        : undefined,
      phoneNumber: fullPhoneNumber,
      notes,
      age,
      acceptedTerms: true,
      insurancePlan,
      promoCode,
      licenseNumber: normalizedLicenseNumber,
      licenseImageUrl: "",
      damageAcknowledged: false,
      beforePhotos: [],
      quote: isSuccessfulHireQuote(quote) ? quote : undefined,
    };
    if (licenseNumber) setLicenseStatus("pending");
    onClose();
    navigate("/car-hire-checkout", { state: { car, hire } });
  };
  const successfulQuote = isSuccessfulHireQuote(quote) ? quote : null;
  const breakdown = successfulQuote?.breakdown;
  const longStayDiscount = roundMoney(Number(breakdown?.longStayDiscount || 0));
  const promoDiscount = roundMoney(Number(breakdown?.promoDiscount || 0));
  const insuranceFee = roundMoney(Number(breakdown?.insuranceFee || 0));
  const quoteDeliveryFee = deliveryRequested
    ? roundMoney(Number(breakdown?.deliveryFee || 0))
    : 0;
  const quotedTotalDue = Number(breakdown?.totalDue || 0);
  const quoteProductSubtotal = roundMoney(
    Math.max(0, quotedTotalDue - quoteDeliveryFee),
  );
  const quoteServiceFee = roundMoney(quoteProductSubtotal * SERVICE_FEE_RATE);
  const quoteCheckoutTotal = roundMoney(
    quoteProductSubtotal + quoteServiceFee + quoteDeliveryFee,
  );
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay bg={theme.overlay} />
      <ModalContent
        borderRadius={cardsBorderRadii}
        bg={theme.surface}
        overflow="visible"
      >
        <ModalHeader color={theme.text}>
          Hire {car.brand || car.make} {car.model}
        </ModalHeader>
        <ModalCloseButton color={theme.text} />
        <ModalBody pb={6} overflow="visible">
          <Box
            mb={4}
            px={3}
            py={2.5}
            border="1px solid"
            borderColor={theme.border}
            borderRadius={cardsBorderRadii}
            bg={theme.surfaceAlt}
          >
            <Text fontSize="sm" color={theme.text} fontWeight="700">
              Pickup and return dates are required before checkout.
            </Text>
            <Text mt={0.5} fontSize="xs" color={theme.textSubtle}>
              Dates selected on the car-hire page are already filled in below.
              You can change them here if needed; you never need to enter the
              same dates twice.
            </Text>
          </Box>
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={4}
            overflow="visible"
          >
            <FormControl
              isRequired
              isInvalid={
                dateValidationAttempted && Boolean(dateValidation.pickupError)
              }
              position="relative"
              zIndex={30}
            >
              <FormLabel color={theme.text}>Pickup date</FormLabel>
              <Box
                border={
                  dateValidationAttempted && dateValidation.pickupError
                    ? "1px solid"
                    : "1px solid transparent"
                }
                borderColor={
                  dateValidationAttempted && dateValidation.pickupError
                    ? theme.danger
                    : "transparent"
                }
                borderRadius={cardsBorderRadii}
                boxShadow={
                  dateValidationAttempted && dateValidation.pickupError
                    ? `0 0 0 1px ${theme.danger}`
                    : "none"
                }
              >
                <CustomDatePicker
                  width={{ base: "100%", md: "100%" }}
                  selected={pickupDate}
                  onChange={(date) => {
                    setPickupDate(date);
                    if (date && returnDate && returnDate <= date) {
                      setReturnDate(null);
                    }
                  }}
                  placeholder="Pickup date"
                  filterDate={filterAvailableDate}
                />
              </Box>
              {dateValidationAttempted && dateValidation.pickupError && (
                <Text
                  mt={1}
                  fontSize="xs"
                  color={theme.danger}
                  fontWeight="600"
                >
                  {dateValidation.pickupError}
                </Text>
              )}
            </FormControl>
            <FormControl isRequired>
              <FormLabel color={theme.text}>Pickup time</FormLabel>
              <CustomTimePicker
                value={pickupTime}
                onChange={setPickupTime}
                isRequired
              />
            </FormControl>
            <FormControl
              isRequired
              isInvalid={
                dateValidationAttempted && Boolean(dateValidation.returnError)
              }
              position="relative"
              zIndex={29}
            >
              <FormLabel color={theme.text}>Return date</FormLabel>
              <Box
                border={
                  dateValidationAttempted && dateValidation.returnError
                    ? "1px solid"
                    : "1px solid transparent"
                }
                borderColor={
                  dateValidationAttempted && dateValidation.returnError
                    ? theme.danger
                    : "transparent"
                }
                borderRadius={cardsBorderRadii}
                boxShadow={
                  dateValidationAttempted && dateValidation.returnError
                    ? `0 0 0 1px ${theme.danger}`
                    : "none"
                }
              >
                <CustomDatePicker
                  width={{ base: "100%", md: "100%" }}
                  selected={returnDate}
                  onChange={setReturnDate}
                  placeholder="Return date"
                  filterDate={filterAvailableDate}
                />
              </Box>
              {dateValidationAttempted && dateValidation.returnError && (
                <Text
                  mt={1}
                  fontSize="xs"
                  color={theme.danger}
                  fontWeight="600"
                >
                  {dateValidation.returnError}
                </Text>
              )}
            </FormControl>
            <FormControl isRequired>
              <FormLabel color={theme.text}>Return time</FormLabel>
              <CustomTimePicker
                value={returnTime}
                onChange={setReturnTime}
                isRequired
              />
            </FormControl>
            <FormControl gridColumn={{ base: "auto", md: "1 / -1" }}>
              <FormLabel color={theme.text}>Pickup address</FormLabel>
              {loadError || !API_KEY ? (
                <Input
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.target.value)}
                  placeholder="Pickup address"
                  {...inputStyles}
                />
              ) : !isLoaded ? (
                <Input
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.target.value)}
                  placeholder="Loading..."
                  {...inputStyles}
                />
              ) : (
                <AutocompleteInput
                  placeholder={car.pickupLocation || "Pickup address"}
                  value={pickupAddress}
                  onPlaceSelected={(addr: string) => setPickupAddress(addr)}
                  onLocationSelected={(coords: Coords) =>
                    setPickupCoords(coords)
                  }
                />
              )}
            </FormControl>
            <FormControl gridColumn={{ base: "auto", md: "1 / -1" }}>
              <HStack justify="space-between">
                <FormLabel mb={0} color={theme.text}>
                  Delivery to customer?
                </FormLabel>
                <Switch
                  isChecked={deliveryRequested}
                  onChange={(e) => setDeliveryRequested(e.target.checked)}
                />
              </HStack>
              {deliveryRequested && (
                <Box mt={2}>
                  {loadError || !API_KEY ? (
                    <Input
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder="Delivery address"
                      {...inputStyles}
                    />
                  ) : !isLoaded ? (
                    <Input
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder="Loading..."
                      {...inputStyles}
                    />
                  ) : (
                    <AutocompleteInput
                      placeholder="Delivery address"
                      value={deliveryAddress}
                      onPlaceSelected={(addr: string) =>
                        setDeliveryAddress(addr)
                      }
                      onLocationSelected={(coords: Coords) =>
                        setDeliveryCoords(coords)
                      }
                    />
                  )}
                </Box>
              )}
            </FormControl>
            <Box pt="2px">
              <AppPhoneInput
                label="Phone number"
                value={phoneNumber}
                countryCode={phoneCountryCode}
                placeholder="0999 123 456"
                onCountryChange={setPhoneCountryCode}
                onNumberChange={setPhoneNumber}
                theme={theme}
                isRequired
              />
              {continueAttempted && phoneNumberError && (
                <Text
                  mt={1}
                  fontSize="xs"
                  color={theme.danger}
                  fontWeight="600"
                >
                  {phoneNumberError}
                </Text>
              )}
            </Box>
            <FormControl isRequired pt="1px">
              <FormLabel color={theme.text} mb={2}>
                Age
              </FormLabel>
              <Input
                type="number"
                value={age}
                onChange={(e) => setAge(Number(e.target.value))}
                {...inputStyles}
              />
            </FormControl>
            <FormControl
              isRequired
              isInvalid={continueAttempted && Boolean(licenseNumberError)}
              gridColumn={{ base: "auto", md: "1 / -1" }}
            >
              <FormLabel color={theme.text}>Driver license</FormLabel>
              <HStack>
                <Input
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  placeholder="License number"
                  {...inputStyles}
                />
                <Badge
                  colorScheme={
                    licenseStatus === "verified"
                      ? "green"
                      : licenseStatus === "pending"
                        ? "yellow"
                        : "gray"
                  }
                >
                  {licenseNumber ? "pending" : "unverified"}
                </Badge>
              </HStack>
              {continueAttempted && licenseNumberError && (
                <Text
                  mt={1}
                  fontSize="xs"
                  color={theme.danger}
                  fontWeight="600"
                >
                  {licenseNumberError}
                </Text>
              )}
            </FormControl>
            <FormControl gridColumn={{ base: "auto", md: "1 / -1" }}>
              <FormLabel color={theme.text}>Insurance</FormLabel>
              <RadioGroup
                value={insurancePlan}
                onChange={(v) => setInsurancePlan(v as any)}
              >
                <Stack direction={{ base: "column", md: "row" }}>
                  <Radio value="none">None</Radio>
                  <Radio value="basic">Basic</Radio>
                  <Radio value="premium">Premium</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
            <FormControl gridColumn={{ base: "auto", md: "1 / -1" }}>
              <FormLabel color={theme.text}>Promo code</FormLabel>
              <Input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="SAVE10"
                {...inputStyles}
              />
            </FormControl>
            <FormControl gridColumn={{ base: "auto", md: "1 / -1" }}>
              <FormLabel color={theme.text}>Notes (optional)</FormLabel>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any instructions?"
                borderColor={theme.inputBorder}
                bg={theme.inputBg}
                color={theme.text}
                _placeholder={{ color: theme.inputPlaceholder }}
                _hover={{ borderColor: theme.inputBorderFocus }}
                _focus={{
                  borderColor: theme.inputBorderFocus,
                  boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                }}
              />
            </FormControl>
          </SimpleGrid>
          <Divider my={5} borderColor={theme.divider} />
          <Box>
            <Text fontWeight="semibold" color={theme.text}>
              Quote
            </Text>
            {loadingQuote ? (
              <Text color={theme.textSubtle}>Calculating…</Text>
            ) : successfulQuote ? (
              <Box
                mt={2}
                p={3}
                border="1px solid"
                borderColor={theme.border}
                borderRadius={cardsBorderRadii}
                bg={theme.surfaceAlt}
              >
                <HStack justify="space-between">
                  <Text color={theme.text}>Days</Text>
                  <Text color={theme.text}>{successfulQuote.days}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color={theme.text}>Base</Text>
                  <Text color={theme.text}>
                    MWK {Number(breakdown?.baseSubtotal || 0).toLocaleString()}
                  </Text>
                </HStack>
                {longStayDiscount > 0 && (
                  <HStack justify="space-between">
                    <Text color={theme.text}>Long-stay</Text>
                    <Text color={theme.text}>
                      - MWK {longStayDiscount.toLocaleString()}
                    </Text>
                  </HStack>
                )}
                {promoDiscount > 0 && (
                  <HStack justify="space-between">
                    <Text color={theme.text}>Promo</Text>
                    <Text color={theme.text}>
                      - MWK {promoDiscount.toLocaleString()}
                    </Text>
                  </HStack>
                )}
                {insuranceFee > 0 && (
                  <HStack justify="space-between">
                    <Text color={theme.text}>Insurance</Text>
                    <Text color={theme.text}>
                      MWK {insuranceFee.toLocaleString()}
                    </Text>
                  </HStack>
                )}
                {quoteDeliveryFee > 0 && (
                  <HStack justify="space-between">
                    <Text color={theme.text}>Delivery</Text>
                    <Text color={theme.text}>
                      MWK {quoteDeliveryFee.toLocaleString()}
                    </Text>
                  </HStack>
                )}
                <HStack justify="space-between">
                  <Text color={theme.text}>PickDengu service fee (5%)</Text>
                  <Text color={theme.text}>
                    MWK {quoteServiceFee.toLocaleString()}
                  </Text>
                </HStack>
                <Divider my={2} borderColor={theme.divider} />
                <HStack justify="space-between">
                  <Text fontWeight="bold" color={theme.text}>
                    Checkout total
                  </Text>
                  <Text fontWeight="bold" color={theme.text}>
                    MWK {quoteCheckoutTotal.toLocaleString()}
                  </Text>
                </HStack>
              </Box>
            ) : isFailedHireQuote(quote) ? (
              <Text color={theme.danger}>{quote.error}</Text>
            ) : (
              <Text color={theme.textSubtle}>Select dates to see quote.</Text>
            )}
          </Box>
          <HStack justify="flex-end" mt={6}>
            <Button
              variant="outline"
              onClick={onClose}
              borderColor={theme.borderStrong}
              color={theme.text}
              _hover={{ bg: theme.hover }}
            >
              Cancel
            </Button>
            <Button
              color={theme.textOnPrimary}
              bgImage={theme.gradients.primaryButton}
              _hover={{ bgImage: theme.gradients.primaryButtonHover }}
              boxShadow={theme.shadowSm}
              onClick={submit}
              isLoading={loadingQuote}
              loadingText="Checking dates"
              borderRadius="5px"
            >
              Continue to Checkout
            </Button>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
